import PropTypes from "prop-types"
import debounce from "lodash/debounce"
import { useNavigate } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import React, { useState, useEffect, useCallback } from "react"

import css from "./Search.scss"
import { getAssetPath, sanitizer } from "@commonUtils"
import { isPwaUrl } from "@lib/urlMatchUtility"
import GaEvents from "@containers/Header/GaEvents"
import Separator from "@components/Separator/Separator"
import ImageLoader from "@components/ImageLoader/ImageLoader"
import ConditionalLink from "@wrappers/ConditionalLink/ConditionalLink"
import { fetchSearchData, resetSearchResults, setSearchTerm } from "./reducer"
import { sendMixpanelTrackEvent } from "@lib/gtmUtility"

const searchIcon = getAssetPath("svgs/icons/search_18.svg")

const onRedirect = (redirectUrl, navigate) => {
    if (redirectUrl?.length)
        if (isPwaUrl(redirectUrl) && navigate) {
            navigate(redirectUrl)
        } else window.location.href = redirectUrl
}

const Dropdown = ({ header, data, onClick }) => {
    return (
        <div className={css.searchDropdown}>
            {header && <div className={`${css.upperCase} col-4 textSecondary padding-16-0`}>{header}</div>}
            {data?.map((item, idx) => (
                <ConditionalLink
                    isExternal={!isPwaUrl(item.slug)}
                    key={item.slug}
                    to={item.slug || `/search/all?name=${item.name}`}
                    className="noAnchorColor"
                    onClick={() => onClick?.(item, idx)}
                >
                    <div
                        className={`${css.dropdownItem} gutter-only padding-16-0`}
                        dangerouslySetInnerHTML={{ __html: sanitizer(item.name) }}
                    />
                    <Separator size="xs" />
                </ConditionalLink>
            ))}
        </div>
    )
}

const Search = () => {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const [query, setQuery] = useState("")
    const { searchResults, searchSuggestions, searchResponseType } = useSelector(
        (state) => state.searchReducer
    )
    const placeholderText = "Search for Medicines and Health Products"

    useEffect(() => {
        if (query.length) {
            debouncedOnChange(query)
        } else {
            dispatch(resetSearchResults())
            debouncedOnChange.cancel()
        }
    }, [query])

    const onChange = (value) => {
        const payload = {
            q: value,
            types: "allopathy,brand,sku,udp,disease",
            per_page: 12,
        }
        dispatch(fetchSearchData(payload))
    }

    const gaOnSubmit = () => {
        if (searchSuggestions.length) {
            GaEvents.onEnterSearch(
                `${query}, did_you_mean, ${searchSuggestions.length}, ${
                    searchSuggestions[0].source || "other"
                }`
            )
        } else {
            GaEvents.onEnterSearch(`${query}, general`)
        }
    }

    const onSearchPress = () => {
        const searchTerm = query.trim() || ""
        if (searchTerm.length) {
            gaOnSubmit()
            onRedirect(`/search/all?name=${searchTerm}`, navigate)
        }
    }

    const onKeyDownHandler = (e) => {
        if (e.keyCode == 13) {
            onSearchPress()
        }
    }

    const onInputFocus = () => {
        GaEvents.onSearchFocus({ page: window.location.pathname })
    }

    const debouncedOnChange = useCallback(debounce(onChange, 300), [])

    const onSearchClick = () => {
        if (!query) {
            sendMixpanelTrackEvent("search_bar_clicked", {
                business_unit: "pharmacy",
            })
        }
    }

    return (
        <div className={`flex alignCenter padding-0-8 ${css.container}`}>
            <label htmlFor="search-medicine" className="visuallyHidden">
                {placeholderText}
            </label>
            <input
                id="search-medicine"
                onClick={onSearchClick}
                className={css.searchInput}
                type="text"
                placeholder={placeholderText}
                title={placeholderText}
                aria-label={placeholderText}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={onKeyDownHandler}
                onFocus={onInputFocus}
            />
            <div className={`padding-0-8 pointer ${css.flexShrink0}`} onClick={onSearchPress}>
                <ImageLoader src={searchIcon} preload={true} width="18px" height="18px" />
            </div>
            {searchResponseType === "RESULTS" && (
                <Dropdown
                    header={searchResults.header}
                    data={searchResults.data}
                    onClick={(item) => setSearchTerm(item.name)}
                />
            )}
            {searchResponseType === "SUGGESTIONS" && (
                <Dropdown
                    header={searchSuggestions.header}
                    data={searchSuggestions.data}
                    onClick={(item, idx) => {
                        const source = item.source === "google" ? item.source : "other"
                        GaEvents.onClickSuggestion(
                            `${query}, ${idx + 1}, did_you_mean, ${item.name}, ${source}`
                        )
                    }}
                />
            )}
            {searchResponseType === "NORESULTS" && <Dropdown header="No results found" />}
        </div>
    )
}

Dropdown.propTypes = {
    header: PropTypes.string,
    data: PropTypes.object,
    onClick: PropTypes.func,
}

export default Search
